import { useRelationDialogOpen } from '@/components/RelationActionMenu/hooks/useRelationDialogOpen';
import { ActionMenus } from '@/features/ap_payees/components/DocInstructionMenu/ActionMenus';
import { ExportInstructionPdfDialog } from '@/features/ap_payees/components/DocInstructionMenu/ExportInstructionsPdf';
import { SendInstructionsEmailDialog } from '@/features/ap_payees/components/DocInstructionMenu/SendInstructionsEmail/SendInstructionsEmailDialog';
import { DocInstructionMenuProps } from '@/features/ap_payees/components/DocInstructionMenu/types';
import { useClosePayeeDrawer } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/useClosePayeeDrawer';
import { useViewerPayeeIdChange } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/useViewerPayeeChange';
import { useGetFlags } from '@/hooks/useFeatureFlag';
import { ActionMenuProps } from '@moneyforward/ap-frontend-components';
import ErrorBoundary from 'antd/es/alert/ErrorBoundary';
import { FC, memo, Suspense, useCallback, useMemo, useState } from 'react';
import { useCheckBulkInvitationAvailabilities } from './Hooks/useCheckBulkInvitationAvailabilities';

const InnerDocInstructionMenu: FC<DocInstructionMenuProps> = memo(
  ({
    onSendEmail,
    disabledButton,
    disabledSendmail,
    disabledExportPdf,
    payees,
    payeeQuery,
  }) => {
    const [openInstructionPdfDialog, setOpenInstructionPdfDialog] =
      useState(false);
    const [openSendEmailDialog, setOpenSendEmailDialog] = useState(false);
    const isPayeeDetailDrawerEnabled = useGetFlags('PAYEE_DETAIL_DRAWER');
    const { isOpen: isPayeeDrawerOpen } = useViewerPayeeIdChange();
    const closePayeeDrawer = useClosePayeeDrawer();
    const {
      handleCheckBulkInvitationAvailabilities,
      isChecking,
      bulkEmailAvailable,
      shouldCheckBulkInvitationAvailabilities,
    } = useCheckBulkInvitationAvailabilities();

    const {
      open: _open,
      onOpen: _onOpen,
      onClose: _onClose,
    } = useRelationDialogOpen();

    const isDisabledSendmail = useMemo(() => {
      if (!isPayeeDetailDrawerEnabled) return disabledSendmail;

      if (bulkEmailAvailable === false) {
        return true;
      }

      if (payeeQuery || !shouldCheckBulkInvitationAvailabilities) {
        return disabledSendmail;
      }

      return !bulkEmailAvailable;
    }, [
      isPayeeDetailDrawerEnabled,
      payeeQuery,
      disabledSendmail,
      bulkEmailAvailable,
      shouldCheckBulkInvitationAvailabilities,
    ]);

    const isDisabledButton = useMemo(() => {
      if (!isPayeeDetailDrawerEnabled) return disabledButton;
      return disabledButton || isChecking;
    }, [isPayeeDetailDrawerEnabled, disabledButton, isChecking]);

    const onOpenChangeWithCheckBulkInvitationAvailabilities = useCallback(
      async (_open: boolean) => {
        if (_open) {
          if (isPayeeDetailDrawerEnabled && isPayeeDrawerOpen) {
            closePayeeDrawer();
          }
          if (payees && payees.length > 0) {
            await handleCheckBulkInvitationAvailabilities(payees);
          }
          _onOpen();
        } else {
          _onClose();
        }
      },
      [
        _onOpen,
        _onClose,
        isPayeeDetailDrawerEnabled,
        isPayeeDrawerOpen,
        closePayeeDrawer,
        handleCheckBulkInvitationAvailabilities,
        payees,
      ]
    );

    const onOpenChange = useCallback(
      (_open: boolean) => {
        if (_open) {
          _onOpen();
          if (isPayeeDetailDrawerEnabled && isPayeeDrawerOpen) {
            closePayeeDrawer();
          }
        } else {
          _onClose();
        }
      },
      [
        _onOpen,
        _onClose,
        isPayeeDetailDrawerEnabled,
        isPayeeDrawerOpen,
        closePayeeDrawer,
      ]
    );

    const handleOpenChange =
      isPayeeDetailDrawerEnabled && shouldCheckBulkInvitationAvailabilities
        ? onOpenChangeWithCheckBulkInvitationAvailabilities
        : onOpenChange;

    const dropDownProps: ActionMenuProps['dropDownProps'] = useMemo(
      () => ({
        open: _open,
        onOpenChange: handleOpenChange,
        destroyPopupOnHide: true,
      }),
      [_open, handleOpenChange]
    );

    const handleOpenInstructionPdfDialog = useCallback(() => {
      setOpenInstructionPdfDialog(true);
      _onClose();
    }, [_onClose, setOpenInstructionPdfDialog]);

    const handleCloseInstructionPdfDialog = useCallback(() => {
      setOpenInstructionPdfDialog(false);
    }, [setOpenInstructionPdfDialog]);

    const handleOpenSendEmailDialog = useCallback(() => {
      setOpenSendEmailDialog(true);
      _onClose();
      if (onSendEmail) {
        onSendEmail();
      }
    }, [_onClose, setOpenSendEmailDialog, onSendEmail]);

    const handleCloseSendEmailDialog = useCallback(() => {
      setOpenSendEmailDialog(false);
    }, [setOpenSendEmailDialog]);

    return (
      <>
        <ActionMenus
          onSendEmail={handleOpenSendEmailDialog}
          disabledSendmail={isDisabledSendmail}
          disabledExportPdf={disabledExportPdf}
          isDisabled={isDisabledButton}
          handleOpenInstructionPdfDialog={handleOpenInstructionPdfDialog}
          dropDownProps={dropDownProps}
          isLoading={isChecking}
        />
        <ExportInstructionPdfDialog
          open={openInstructionPdfDialog}
          onClose={handleCloseInstructionPdfDialog}
          payees={payees}
          payeeQuery={payeeQuery}
        />
        <SendInstructionsEmailDialog
          open={openSendEmailDialog}
          onClose={handleCloseSendEmailDialog}
          payees={payees}
          payeeQuery={payeeQuery}
        />
      </>
    );
  }
);

InnerDocInstructionMenu.displayName = 'InnerDocInstructionMenu';

export const DocInstructionMenu: FC<DocInstructionMenuProps> = memo(
  ({
    disabledButton,
    disabledSendmail,
    disabledExportPdf,
    onSendEmail,
    payees,
    payeeQuery,
  }) => {
    return (
      <ErrorBoundary>
        <Suspense fallback={null}>
          <InnerDocInstructionMenu
            onSendEmail={onSendEmail}
            disabledButton={disabledButton}
            disabledSendmail={disabledSendmail}
            disabledExportPdf={disabledExportPdf}
            payees={payees}
            payeeQuery={payeeQuery}
          />
        </Suspense>
      </ErrorBoundary>
    );
  }
);

DocInstructionMenu.displayName = 'DocInstructionMenu';
